import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
    Splitter, Spin, Button, Typography, Select, Input, Form, Space, Divider,
    Collapse, Modal, Tag, Popover, Popconfirm, message,
} from 'antd';
import {
    PlusOutlined, CopyOutlined, UserOutlined, GlobalOutlined, LayoutOutlined,
    DownloadOutlined, FileImageOutlined, FilePdfOutlined,
} from '@ant-design/icons';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';

import FlowCanvas from '../flow-canvas/FlowCanvas';
import '../flow-canvas/FlowCanvas.css';
import ModuleDetailPanel from '../module-group/ModuleDetailPanel';
import '../module-group/view2.css';
import { FlowStoreProvider } from '../../stores/flow-store-context';
import useBotBuilderStore from '../../stores/useBotBuilderStore';
import './BuildBotV2.css';
import Globals from './globals';
import GlobalErrors from './global-errors';
import UploadLogo from './upload-logo';
import PersistentMenu from './persistent-menu';
import ColoredQRCode from '../apply-by-text/ColoredQRCode';
import scanToApplyIcon from '../../assets/qr/scantoapply.png';
import UserAvatar from '../common/UserAvatar';
import { CloseOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const CANVAS_EXTRAS = { pathForks: true, botRedirect: true, tags: true };

// ── Create Bot modal ───────────────────────────────────
function CreateBotModal({ open, onClose, selectedCompany }) {
    const [form] = Form.useForm();
    const { createBot, companyList, availableNumbers } = useBotBuilderStore();
    const [botType, setBotType] = useState('');

    // Lock-in the active company from the main layout if one is selected
    useEffect(() => {
        if (open && selectedCompany) {
            form.setFieldsValue({ company: selectedCompany });
        }
    }, [open, selectedCompany, form]);

    const handleOk = async () => {
        const values = await form.validateFields().catch(() => null);
        if (!values) return;
        await createBot({
            name: values.name,
            mobile: values.mobile,
            type: values.type,
            page_token: values.type === 'messenger' ? values.page_token || '' : '',
            company: selectedCompany || values.company,
        });
        form.resetFields();
        setBotType('');
        onClose();
    };

    const lockedCompanyName = selectedCompany
        ? companyList.find((c) => c.id === selectedCompany)?.name
        : null;

    return (
        <Modal
            title="Create a Bot"
            open={open}
            onCancel={onClose}
            onOk={handleOk}
            okText="Create"
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onValuesChange={(c) => c.type !== undefined && setBotType(c.type)}
                initialValues={{ company: selectedCompany || undefined }}
            >
                <Form.Item name="name" label="Bot name" rules={[{ required: true }]}>
                    <Input placeholder="Name" />
                </Form.Item>
                <Form.Item name="type" label="Bot type" rules={[{ required: true }]}>
                    <Select placeholder="Select type">
                        <Option value="twilio">Twilio</Option>
                        <Option value="messenger">Messenger</Option>
                        <Option value="webchat">Webchat</Option>
                        <Option value="messagemedia">Message Media</Option>
                        <Option value="nexmo">Nexmo</Option>
                        <Option value="nexmo-broadcast">Nexmo Broadcast Campaign</Option>
                    </Select>
                </Form.Item>
                <Form.Item name="mobile" label="Bot SMS / Number">
                    {(botType === 'nexmo' || botType === 'nexmo-broadcast') ? (
                        <Select placeholder="Select number" allowClear showSearch optionFilterProp="children">
                            {availableNumbers.map((n) => (
                                <Option key={n.id} value={n.id}>
                                    {n.mobile} [{n.status}]
                                </Option>
                            ))}
                        </Select>
                    ) : (
                        <Input placeholder="Phone or identifier" />
                    )}
                </Form.Item>
                {botType === 'messenger' && (
                    <Form.Item name="page_token" label="Messenger Page Token">
                        <Input />
                    </Form.Item>
                )}
                <Form.Item
                    name="company"
                    label="Company"
                    extra={selectedCompany ? 'Using the company selected in the top bar' : null}
                    rules={selectedCompany ? undefined : [{ required: true, message: 'Select a company' }]}
                >
                    <Select
                        placeholder="Select company"
                        allowClear={!selectedCompany}
                        disabled={!!selectedCompany}
                        showSearch
                        optionFilterProp="children"
                    >
                        {selectedCompany && lockedCompanyName ? (
                            <Option key={selectedCompany} value={selectedCompany}>{lockedCompanyName}</Option>
                        ) : (
                            companyList.map((c) => (
                                <Option key={c.id} value={c.id}>{c.name}</Option>
                            ))
                        )}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
}

// ── Copy Bot modal ─────────────────────────────────────
function CopyBotModal({ open, onClose }) {
    const { copyBot, botList } = useBotBuilderStore();
    const [sourceId, setSourceId] = useState('');
    const [destId, setDestId] = useState('');

    const reset = () => {
        setSourceId('');
        setDestId('');
    };

    const handleOk = async () => {
        if (!sourceId || !destId || sourceId === destId) return;
        await copyBot(sourceId, destId);
        reset();
        onClose();
    };

    return (
        <Modal
            title="Copy a Bot"
            open={open}
            onCancel={() => { reset(); onClose(); }}
            onOk={handleOk}
            okText="Copy"
            okButtonProps={{ disabled: !sourceId || !destId || sourceId === destId }}
            destroyOnClose
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                    <Text>Source bot</Text>
                    <Select
                        placeholder="Select source bot"
                        value={sourceId || undefined}
                        onChange={setSourceId}
                        style={{ width: '100%', marginTop: 4 }}
                        showSearch
                        optionFilterProp="children"
                    >
                        {botList.map((b) => (
                            <Option key={b.id} value={b.id}>{b.name}</Option>
                        ))}
                    </Select>
                </div>
                <div>
                    <Text>Destination bot</Text>
                    <Select
                        placeholder="Select destination bot"
                        value={destId || undefined}
                        onChange={setDestId}
                        style={{ width: '100%', marginTop: 4 }}
                        showSearch
                        optionFilterProp="children"
                    >
                        {botList.filter((b) => b.id !== sourceId).map((b) => (
                            <Option key={b.id} value={b.id}>{b.name}</Option>
                        ))}
                    </Select>
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    This replaces the destination bot's modules with a copy of the source bot's flow.
                </Text>
            </Space>
        </Modal>
    );
}

// ── General section (assign user) ──────────────────────
function GeneralSection() {
    const { bot, users, botUsers, assignUser, unassignUser } = useBotBuilderStore();
    const [userId, setUserId] = useState('');

    if (!bot) return null;

    const assignedIds = new Set((botUsers || []).map((u) => u.id));
    const selectableUsers = (users || []).filter((u) => !assignedIds.has(u.id));

    const handleAssign = async () => {
        if (!userId) return;
        await assignUser(userId);
        setUserId('');
    };

    return (
        <div>
            <Text strong style={{ fontSize: 13 }}>
                <UserOutlined style={{ marginRight: 6 }} />
                Assigned users ({botUsers.length})
            </Text>
            {botUsers.length > 0 ? (
                <Space size={6} wrap style={{ display: 'flex', marginTop: 6, marginBottom: 4 }}>
                    {botUsers.map((u) => (
                        <div key={u.id} className="bb-user-pill">
                            <UserAvatar
                                name={u.name}
                                src={u.avatar_url}
                                userId={u.id}
                                size={22}
                                showTooltip={false}
                            />
                            <span className="bb-user-pill-name">{u.name}</span>
                            <Popconfirm
                                title={`Are you sure you want to unassign ${u.name}?`}
                                onConfirm={() => unassignUser(u.id)}
                                okText="Unassign"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <button type="button" className="bb-user-pill-remove" aria-label={`Unassign ${u.name}`}>
                                    <CloseOutlined />
                                </button>
                            </Popconfirm>
                        </div>
                    ))}
                </Space>
            ) : (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, margin: '4px 0' }}>
                    No users assigned yet.
                </Text>
            )}
            <Divider style={{ margin: '8px 0' }} />
            <Text strong style={{ fontSize: 13 }}>Add user</Text>
            <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                <Select
                    placeholder={selectableUsers.length ? 'Select user' : 'No users available'}
                    value={userId || undefined}
                    onChange={setUserId}
                    style={{ flex: 1 }}
                    showSearch
                    optionFilterProp="children"
                    disabled={selectableUsers.length === 0}
                >
                    {selectableUsers.map((u) => (
                        <Option key={u.id} value={u.id}>{u.name}</Option>
                    ))}
                </Select>
                <Button type="primary" onClick={handleAssign} disabled={!userId}>
                    Assign
                </Button>
            </Space.Compact>
        </div>
    );
}

// ── Globals section (overrides + keywords + errors) ────
function GlobalsSection() {
    const { bot, modules, saveOverrides } = useBotBuilderStore();
    const [stopR, setStopR] = useState('');
    const [unstopR, setUnstopR] = useState('');
    const [helpR, setHelpR] = useState('');

    useEffect(() => {
        if (bot) {
            setStopR(bot.stop_response || '');
            setUnstopR(bot.unstop_response || '');
            setHelpR(bot.help_response || '');
        }
    }, [bot]);

    if (!bot) return null;

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
                <Text strong style={{ fontSize: 13 }}>Response overrides</Text>
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                    Leave blank to use system defaults.
                </Text>
                <Space direction="vertical" style={{ width: '100%' }} size={6}>
                    <Input addonBefore="STOP" value={stopR} onChange={(e) => setStopR(e.target.value)} />
                    <Input addonBefore="UNSTOP" value={unstopR} onChange={(e) => setUnstopR(e.target.value)} />
                    <Input addonBefore="HELP" value={helpR} onChange={(e) => setHelpR(e.target.value)} />
                    <Button
                        type="primary"
                        onClick={() => saveOverrides({
                            stop_response: stopR.trim(),
                            unstop_response: unstopR.trim(),
                            help_response: helpR.trim(),
                        })}
                    >
                        Save overrides
                    </Button>
                </Space>
            </div>

            <Divider style={{ margin: 0 }} />

            <div>
                <Text strong style={{ fontSize: 13 }}>Global keywords</Text>
                <div style={{ marginTop: 6 }}>
                    <Globals bot={bot} modules={modules} />
                </div>
            </div>

            <Divider style={{ margin: 0 }} />

            <div>
                <Text strong style={{ fontSize: 13 }}>Global error phrases</Text>
                <div style={{ marginTop: 6 }}>
                    <GlobalErrors bot={bot} />
                </div>
            </div>
        </Space>
    );
}

// ── Web Chat section ───────────────────────────────────
function WebChatSection() {
    const { bot } = useBotBuilderStore();
    if (!bot || bot.type !== 'webchat') return null;
    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <UploadLogo bot={bot} />
            <Divider style={{ margin: 0 }} />
            <PersistentMenu bot={bot} />
        </Space>
    );
}

const sanitizeMobile = (raw) => {
    // Strip manual annotations like " - deleted 12/22/21" from the mobile string
    const digits = String(raw || '').replace(/[^0-9+]/g, '');
    return digits || null;
};

// Pick the ABT botmap entry — either from the bot itself or from its parent
const pickAbtBotmap = (bot) => {
    if (!bot) return null;
    const own = bot.botmap && bot.botmap[0];
    if (own) {
        if (own.type === 'webchat') return null;
        if (!own.default && own.mobile && !String(own.mobile).startsWith('GH-')) return own;
        return null;
    }
    // Child bot — inherit from parent's botmap
    const parent = bot.parent && bot.parent.botmap && bot.parent.botmap[0];
    if (parent && !parent.default && parent.mobile && !String(parent.mobile).startsWith('GH-')) {
        return parent;
    }
    return null;
};

// Detect bots that are ABT (apply-by-text) — same rule as the bot-selector filter.
// Falls through to the parent bot's botmap for child bots.
const getAbtMobile = (bot) => {
    const bm = pickAbtBotmap(bot);
    return bm ? sanitizeMobile(bm.mobile) : null;
};

const DEFAULT_SCAN_TEXT = 'Click Send to Apply';

// ── Downloadable QR code for ABT bots ─────────────────
function BotQRCode({ bot }) {
    const { updateBot } = useBotBuilderStore();
    const mobile = getAbtMobile(bot);
    const qrRef = useRef(null);
    const [exporting, setExporting] = useState(false);
    const [saving, setSaving] = useState(false);
    // Scan text = the body prefilled in the SMS composer when the QR is
    // scanned. Persisted on bot.scan_text; empty falls back to the
    // "Click Send to Apply" default used by backend gen-qr.js.
    const persistedScanText = (bot.scan_text && bot.scan_text.trim()) || '';
    const [scanText, setScanText] = useState(persistedScanText);

    // Reset local state when the selected bot changes
    useEffect(() => {
        setScanText((bot.scan_text && bot.scan_text.trim()) || '');
    }, [bot.id, bot.scan_text]);

    if (!mobile) return null;

    // For multi-tenant child ABT bots, the keyword is what routes an incoming
    // SMS to this specific child. If the admin hasn't set a custom scan_text,
    // fall through to the keyword so scanning the QR hits the correct bot.
    const keyword = (bot.keyword && bot.keyword.trim()) || '';
    const body = scanText.trim() || keyword || DEFAULT_SCAN_TEXT;
    const value = `sms:${mobile}?body=${encodeURIComponent(body)}`;
    const isDirty = scanText.trim() !== persistedScanText;
    const blankFallbackLabel = keyword
        ? `Blank uses keyword "${keyword}"`
        : `Blank uses "${DEFAULT_SCAN_TEXT}"`;

    const handleSaveScanText = async () => {
        setSaving(true);
        try {
            await updateBot({ scan_text: scanText.trim() });
        } finally {
            setSaving(false);
        }
    };
    const baseFilename = `abt-qr-${(bot.name || 'bot').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

    const withSvg = (cb) => {
        const svgEl = qrRef.current?.querySelector('svg');
        if (!svgEl) {
            message.error('QR code not found.');
            return;
        }
        cb(svgEl);
    };

    const downloadSvg = () => withSvg((svgEl) => {
        // The live SVG is sized for the sidebar (96px). Clone and upscale so the
        // exported file opens at a usable print size — viewBox already carries
        // the geometry, so enlarging width/height just changes the default
        // rendered size without distortion.
        const clone = svgEl.cloneNode(true);
        clone.setAttribute('width', '1024');
        clone.setAttribute('height', '1024');
        if (!clone.getAttribute('xmlns')) clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        const svgData = new XMLSerializer().serializeToString(clone);
        const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        saveAs(blob, `${baseFilename}.svg`);
    });

    const downloadPng = () => withSvg((svgEl) => {
        setExporting(true);
        const svgData = new XMLSerializer().serializeToString(svgEl);
        const url = URL.createObjectURL(new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' }));
        const img = new Image();
        img.onload = () => {
            const scale = 4;
            const canvas = document.createElement('canvas');
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext('2d');
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            canvas.toBlob((blob) => {
                saveAs(blob, `${baseFilename}.png`);
                setExporting(false);
            }, 'image/png');
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            message.error('Failed to generate PNG.');
            setExporting(false);
        };
        img.src = url;
    });

    const downloadPdf = () => withSvg((svgEl) => {
        setExporting(true);
        const svgData = new XMLSerializer().serializeToString(svgEl);
        const url = URL.createObjectURL(new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' }));
        const img = new Image();
        img.onload = () => {
            const scale = 4;
            const canvas = document.createElement('canvas');
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext('2d');
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            const dataUrl = canvas.toDataURL('image/png');
            const doc = new jsPDF({ orientation: 'p', unit: 'in', format: 'letter' });
            const qrSize = 4;
            const x = (8.5 - qrSize) / 2;
            const y = (11 - qrSize) / 2;
            doc.addImage(dataUrl, 'PNG', x, y, qrSize, qrSize);
            doc.save(`${baseFilename}.pdf`);
            setExporting(false);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            message.error('Failed to generate PDF.');
            setExporting(false);
        };
        img.src = url;
    });

    const popover = (
        <Space direction="vertical" size="small" style={{ width: 280 }}>
            <div>
                <Text strong style={{ fontSize: 13 }}>Message on scan</Text>
                <Space.Compact style={{ width: '100%', marginTop: 4 }}>
                    <Input
                        size="small"
                        placeholder={DEFAULT_SCAN_TEXT}
                        value={scanText}
                        onChange={(e) => setScanText(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                        size="small"
                        type="primary"
                        onClick={handleSaveScanText}
                        disabled={!isDirty}
                        loading={saving}
                    >
                        Save
                    </Button>
                </Space.Compact>
                <Text type="secondary" style={{ fontSize: 11 }}>
                    Prefills the SMS composer when the QR is scanned. {blankFallbackLabel}.
                </Text>
            </div>
            <Divider style={{ margin: '4px 0' }} />
            <Text strong style={{ fontSize: 13 }}>Download QR Code</Text>
            <Space size="small">
                <Button size="small" icon={<DownloadOutlined />} onClick={downloadSvg}>SVG</Button>
                <Button size="small" icon={<FileImageOutlined />} onClick={downloadPng} loading={exporting}>PNG</Button>
                <Button size="small" icon={<FilePdfOutlined />} onClick={downloadPdf} loading={exporting}>PDF</Button>
            </Space>
        </Space>
    );

    return (
        <Popover content={popover} trigger="click" placement="bottomLeft">
            <div
                ref={qrRef}
                className="bb-qr-container"
                title="Click for download options"
            >
                <ColoredQRCode
                    value={value}
                    size={96}
                    errorLevel="H"
                    color="#000000"
                    bgColor="#ffffff"
                    finderColor="#38b549"
                    icon={scanToApplyIcon}
                    iconSize={26}
                />
            </div>
        </Popover>
    );
}

// ── Rich info panel (right sidebar, no module selected) ─
function BotInfoPanelRich({ onCreateModule }) {
    const { bot } = useBotBuilderStore();

    if (!bot) {
        return (
            <div className="detail-panel-content">
                <Text type="secondary">Select a bot to start editing its flow.</Text>
            </div>
        );
    }

    const ownBm = bot.botmap && bot.botmap[0];
    const parentBm = bot.parent && bot.parent.botmap && bot.parent.botmap[0];
    const isChild = !ownBm && !!parentBm;
    const botType = (ownBm && ownBm.type) || (isChild ? 'abt' : bot.type);
    const botMobile = (ownBm && ownBm.mobile) || (parentBm && parentBm.mobile) || '';
    const botKeyword = (bot.keyword && bot.keyword.trim()) || '';

    const items = [
        {
            key: 'general',
            label: (<><UserOutlined style={{ marginRight: 6 }} />General</>),
            children: <GeneralSection />,
        },
        {
            key: 'globals',
            label: (<><GlobalOutlined style={{ marginRight: 6 }} />Globals</>),
            children: <GlobalsSection />,
        },
    ];

    if (bot.type === 'webchat') {
        items.push({
            key: 'webchat',
            label: (<><LayoutOutlined style={{ marginRight: 6 }} />Web Chat</>),
            children: <WebChatSection />,
        });
    }

    return (
        <div className="detail-panel-content">
            <div className="bb-info-header">
                <BotQRCode bot={bot} />
                <div className="bb-info-header-meta">
                    <Title level={5} style={{ margin: '0 0 4px' }}>{bot.name}</Title>
                    <Space size={4} wrap>
                        {botType && <Tag color="blue" style={{ fontSize: 11 }}>{botType}</Tag>}
                        {botKeyword && <Tag color="purple" style={{ fontSize: 11 }}>#{botKeyword}</Tag>}
                        {botMobile && <Text type="secondary" style={{ fontSize: 12 }}>{botMobile}</Text>}
                    </Space>
                    {isChild && bot.parent?.name && (
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                            via parent: {bot.parent.name}
                        </Text>
                    )}
                </div>
            </div>
            <Divider style={{ margin: '12px 0' }} />
            <Button type="primary" icon={<PlusOutlined />} onClick={onCreateModule} block>
                Create new module
            </Button>
            <Collapse
                size="small"
                defaultActiveKey={['general']}
                items={items}
                style={{ marginTop: 16 }}
            />
        </div>
    );
}

// ── Main page ──────────────────────────────────────────
function BuildBotV2Inner({ selectedCompany }) {
    const {
        loading,
        bot,
        modules,
        selectedModuleId,
        botList,
        fetchBotList,
        fetchCompanies,
        fetchVonageNumbers,
        fetchBot,
        setSelectedModuleId,
        saveFlowMeta,
    } = useBotBuilderStore();

    const [activeBotId, setActiveBotId] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [copyOpen, setCopyOpen] = useState(false);

    useEffect(() => {
        fetchBotList();
        fetchCompanies();
        fetchVonageNumbers();
    }, [fetchBotList, fetchCompanies, fetchVonageNumbers]);

    useEffect(() => {
        if (activeBotId) fetchBot(activeBotId);
    }, [activeBotId, fetchBot]);

    // Only surface webchat bots and Apply-By-Text (ABT) bots. ABT bots have a
    // botmap with no `default` tag (ie. not a global longcode/shortcode) and
    // their mobile doesn't use the GH- shortcode prefix. Child bots (no botmap
    // but with parent_bot set) inherit their parent's routing and are treated
    // as ABT too.
    const filteredBots = useMemo(() => {
        const isWebchatOrAbt = (b) => {
            const bm = (b.botmap && b.botmap[0]) || null;
            if (bm?.type === 'webchat') return true;
            if (bm && !bm.default && bm.mobile && !String(bm.mobile).startsWith('GH-')) return true;
            if (!bm && b.parent_bot) return true;
            return false;
        };
        const base = botList.filter(isWebchatOrAbt);
        if (!selectedCompany) return base;
        return base.filter((b) => b.company_id === selectedCompany);
    }, [botList, selectedCompany]);

    // Clear selection when the selected bot no longer matches the active company filter
    useEffect(() => {
        if (!activeBotId) return;
        if (!filteredBots.some((b) => b.id === activeBotId)) {
            setActiveBotId('');
        }
    }, [filteredBots, activeBotId]);

    const handleNodeSelect = (moduleId) => setSelectedModuleId(moduleId);

    return (
        <div className="build-bot-v2">
            <div className="bb-action-bar">
                <Space size="small">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setCreateOpen(true)}
                    >
                        New bot
                    </Button>
                    <Button
                        icon={<CopyOutlined />}
                        onClick={() => setCopyOpen(true)}
                        disabled={botList.length < 2}
                    >
                        Copy bot
                    </Button>
                </Space>

                <Select
                    placeholder="Select a bot…"
                    style={{ minWidth: 320, marginLeft: 12 }}
                    showSearch
                    optionFilterProp="children"
                    value={activeBotId || undefined}
                    onChange={setActiveBotId}
                    allowClear
                >
                    {filteredBots.map((b) => {
                        const own = (b.botmap && b.botmap[0]) || null;
                        const parentBm = b.parent && b.parent.botmap && b.parent.botmap[0];
                        let label;
                        if (own && own.type) {
                            label = `${own.type} · ${b.name}${own.mobile ? ` [${own.mobile}]` : ''}`;
                        } else if (parentBm && parentBm.mobile) {
                            const kw = (b.keyword && b.keyword.trim()) ? ` / ${b.keyword.trim()}` : '';
                            label = `abt · ${b.name} [${parentBm.mobile}${kw}]`;
                        } else {
                            label = b.name;
                        }
                        return <Option key={b.id} value={b.id}>{label}</Option>;
                    })}
                </Select>

                {bot && (
                    <div className="bb-breadcrumb">
                        {bot.company && (
                            <Text type="secondary" style={{ fontSize: 13 }}>{bot.company.name} /</Text>
                        )}
                        <Title level={5} style={{ margin: 0 }}>{bot.name}</Title>
                    </div>
                )}

                <div className="bb-action-bar-right">
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {modules.length} module{modules.length !== 1 ? 's' : ''}
                    </Text>
                </div>
            </div>

            <Splitter style={{ flex: 1, minHeight: 0 }}>
                <Splitter.Panel defaultSize="68%" min="55%" max="80%">
                    {!bot ? (
                        <div className="mg-v2-loading">
                            <Text type="secondary">Select a bot to see its flow.</Text>
                        </div>
                    ) : loading ? (
                        <div className="mg-v2-loading">
                            <Spin tip="Loading bot..." />
                        </div>
                    ) : (
                        <FlowCanvas
                            modules={modules}
                            startModuleId={bot.start_module}
                            savedFlowMeta={bot.flow_meta}
                            selectedModuleId={selectedModuleId}
                            onNodeSelect={handleNodeSelect}
                            onSaveLayout={saveFlowMeta}
                            extras={CANVAS_EXTRAS}
                        />
                    )}
                </Splitter.Panel>
                <Splitter.Panel defaultSize="32%">
                    <div className="mg-v2-detail-panel">
                        <ModuleDetailPanel InfoPanel={BotInfoPanelRich} />
                    </div>
                </Splitter.Panel>
            </Splitter>

            <CreateBotModal open={createOpen} onClose={() => setCreateOpen(false)} selectedCompany={selectedCompany} />
            <CopyBotModal open={copyOpen} onClose={() => setCopyOpen(false)} />
        </div>
    );
}

function BuildBotV2(props) {
    return (
        <FlowStoreProvider value={useBotBuilderStore}>
            <BuildBotV2Inner {...props} />
        </FlowStoreProvider>
    );
}

const mapStateToProps = (state) => ({
    selectedCompany: state.company?.selectedCompany,
});

export default connect(mapStateToProps)(BuildBotV2);
